import * as React from "react";
import { FC, useEffect, useMemo, useCallback, useState } from "react";
import _ from "lodash";
import { connect, Dispatch } from "react-redux";
import { FileWithPreview } from "react-dropzone";
import { format } from "lib/globalize-util";
import type { IScope } from "reducers/scope";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { MediaDialog } from "components/dialog/media-dialog";
import { IStore } from "reducers";
import { IItemRecord } from "records/item/item";
import { IProductMedia } from "constants/product-media";
import NotificationError from "components/notification-banners";
import { Uploader360 } from "components/product-media/components/uploader-360";
import { MEDIA_TYPE_360 } from "constants/media";
import { IAsset } from "constants/assets";
import { ContentLoading } from "components/content-loading";
import { IMediaAsset, IMediaAssetMap } from "constants/media-asset";
import {
    fetchMediaAssets,
    save,
    mutateMediaAssets,
    resetProductMediaAssets,
    resetProductMedia,
    upload360,
} from "actions/product-media-actions";
import { createNewMediaKey, transformMediaMap } from "helpers/manage-media";

// prettier-ignore
interface IProps {
    product: IItemRecord;
    open: boolean;
    onClose (reload?: boolean): void;
}

interface IReduxProps {
    asset: IAsset;
    error: string | Error;
    isUploading: boolean;
    mediaAssetMap: IMediaAssetMap;
    revision: string;
    isUpdating: boolean;
    isFetching: boolean;
    scope: IScope;
}

// prettier-ignore
interface IDispatchProps {
    resetProductMedia(): void;
    fetchProductMedia(id: string): void;
    onUpdate (product: string, assets: string[]): void;
    updateMediaAssets(productId: string, revision: string, mediaMap: IMediaAssetMap): void;
    onUpload (product: string, ids: string[]): void;
    onReset (): void;
}

const Upload360Dialog: FC<IProps & IReduxProps & IDispatchProps> = (props) => {
    const { product, open, onClose, scope } = props;
    const { asset, error, onReset, mediaAssetMap, revision } = props;
    const {
        onUpload,
        isUploading,
        isUpdating,
        isFetching,
        fetchProductMedia,
        resetProductMedia,
        updateMediaAssets,
        onUpdate,
    } = props;

    const mediaMap = useMemo(() => transformMediaMap(mediaAssetMap), [mediaAssetMap]);

    const [directUploading, setDirectUploading] = useState<boolean>(false); // internal flag of uploader processing
    const [uploadError, setUploadError] = useState<string>(null);
    const [close, setClose] = useState<boolean>(false); // close the dialog right after update is finished

    const useUnlimitedImages = useMemo(() => scope.brand?.getPreference?.("unlimited_images"), [scope]);
    const media = useMemo<IProductMedia>(
        () => _.find(product.getMedia(scope), { type: MEDIA_TYPE_360 }),
        [product, scope]
    );
    const errorMessage = useMemo(() => format("There were issues with the image(s) you uploaded TEXT"), []);

    useEffect(() => {
        fetchProductMedia(product._id);
    }, [product]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => setUploadError(error && errorMessage), [error, errorMessage]);

    const handleClose = useCallback(
        (reload?: boolean) => {
            onReset(); // reset uploading state
            resetProductMedia();
            setUploadError(null);
            onClose(reload);
        },
        [onClose, onReset, resetProductMedia]
    );

    const handleUpload = useCallback(
        (results: Array<[{ id: string }, FileWithPreview]>) => {
            if (!_.isEmpty(results)) {
                onUpload(product._id, _.map(results, "0.id"));
            }
        },
        [product, onUpload]
    );

    const handleSave = useCallback(() => {
        if (asset) {
            if (useUnlimitedImages) {
                const assetKey = createNewMediaKey(mediaMap);
                updateMediaAssets(product._id, revision, {
                    ...mediaMap,
                    [assetKey]: { ...asset, type: MEDIA_TYPE_360 } as IMediaAsset,
                });
            } else {
                onUpdate(product._id, product.getMediaUpdate(MEDIA_TYPE_360, asset.asset_id));
            }
            setClose(true);
        } else {
            handleClose();
        }
    }, [product, asset, revision, mediaMap, updateMediaAssets, onUpdate, handleClose]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCloseDialog = useCallback(() => handleClose(false), [handleClose]);

    const handleDelete = useCallback(() => {
        onUpdate(product._id, product.getMediaUpdate(MEDIA_TYPE_360));
        setClose(true);
    }, [product, onUpdate]);

    const handleReject = useCallback(() => setUploadError(errorMessage), [errorMessage]);
    const handleUploadStart = useCallback(() => setUploadError(null), []);
    const handleHideError = useCallback(() => setUploadError(null), []);
    const handleError = useCallback(
        ({ message }: { message: string } | Error) => setUploadError(message || errorMessage),
        [errorMessage]
    );

    useEffect(() => {
        if (close && !isUpdating) {
            if (!error) {
                handleClose(true);
            } else {
                setClose(false);
            }
        }
    }, [close, isUpdating, handleClose]); // eslint-disable-line react-hooks/exhaustive-deps

    const classes = useStyles({});
    return (
        <MediaDialog
            open={open}
            title={format("Add 360 Images")}
            onClose={handleCloseDialog}
            onSave={handleSave}
            onDelete={!!media && !useUnlimitedImages && handleDelete}
            disabled={directUploading || isUploading || isUpdating || isFetching}
        >
            <div className={classes.contentTitle}>{format("Review the image specifications TEXT")}</div>
            <NotificationError classes={{ root: classes.error }} message={uploadError} onClose={handleHideError} />
            <Uploader360
                success={!!asset}
                loading={directUploading || isUploading}
                onUpload={handleUpload}
                onUploadError={handleError}
                onDropReject={handleReject}
                onUploadStart={handleUploadStart}
                onLoading={setDirectUploading}
                scope={scope}
            />
            {isUpdating && <ContentLoading className={classes.loading} />}
        </MediaDialog>
    );
};

const useStyles = makeStyles({
    contentTitle: {
        fontSize: 14,
        padding: 10,
    },
    error: {
        margin: 10,
    },
    loading: {
        left: -24,
    },
});

const mapStateToProps = (store: IStore): IReduxProps => ({
    asset: store.productMedia.payload,
    error: store.productMedia.error,
    isUploading: store.productMedia.isUploading,
    mediaAssetMap: store.productMediaAssets?.payload?.media as IMediaAssetMap,
    isFetching: store.productMediaAssets?.isFetching,
    isUpdating: store.productMediaAssets?.isUpdating,
    revision: store.productMediaAssets?.payload?.revision,
    scope: store.scope,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>): IDispatchProps => ({
    fetchProductMedia: (productId: string) => dispatch(fetchMediaAssets(productId)),
    resetProductMedia: () => dispatch(resetProductMediaAssets()),
    onUpdate: (product: string, assets: string[]) => dispatch(save(product, assets)),
    updateMediaAssets: (productId: string, revision: string, mediaMap: IMediaAssetMap) =>
        dispatch(mutateMediaAssets(productId, revision, mediaMap)),
    onUpload: (product: string, ids: string[]) => dispatch(upload360(product, ids)),
    onReset: () => dispatch(resetProductMedia()),
});

export default connect<IReduxProps, IDispatchProps, IProps>(mapStateToProps, mapDispatchToProps)(Upload360Dialog);
